import {
  Button,
  IconButton,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import TopNav from "../components/TopNav";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Arrow,
  Image as KImage,
  Layer,
  Line,
  Stage,
  Text,
} from "react-konva";
import { useEffect, useRef, useState } from "react";
import pageStyle from "./PhotoPage.module.css";
import { deletePhoto, getPhoto, savePhoto } from "../api/photos";
import {
  ArrowBack,
  AutoFixNormal,
  Delete,
  DeleteSweep,
  Gesture,
  NorthEast,
  Photo,
  Rotate90DegreesCw,
  TextFields,
  Undo,
} from "@mui/icons-material";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Photos are kept at most this many pixels on the long side, which keeps
// saved photos small enough to sync quickly from the field.
const MAX_PHOTO_SIZE = 2000;

const COLORS = ["#e53935", "#fdd835", "#43a047", "#1e88e5", "#ffffff", "#000000"];

// Line thickness as a fraction of the photo's long side, so markings look
// the same on any photo.
const SIZES = { S: 0.004, M: 0.008, L: 0.016 };

type Tool = "pen" | "arrow" | "text" | "eraser";

// Markings are stored in photo pixels, not screen pixels, so they line up
// at any screen size, survive rotation, and export at full resolution.
type Shape =
  | { type: "pen" | "eraser"; points: number[]; color: string; width: number }
  | { type: "arrow"; points: number[]; color: string; width: number }
  | { type: "text"; x: number; y: number; text: string; color: string; width: number };

function loadPhoto(src: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(
        1,
        MAX_PHOTO_SIZE / Math.max(img.naturalWidth, img.naturalHeight)
      );
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas);
    };
    img.onerror = reject;
    img.src = src;
  });
}

function rotateCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = source.height;
  canvas.height = source.width;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(canvas.width, 0);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(source, 0, 0);
  return canvas;
}

// Rotates markings 90° clockwise along with a photo of the given height.
function rotateShape(shape: Shape, height: number): Shape {
  const rotate = (x: number, y: number) => [height - y, x];
  if (shape.type === "text") {
    const [x, y] = rotate(shape.x, shape.y);
    return { ...shape, x, y };
  }
  const points: number[] = [];
  for (let i = 0; i < shape.points.length; i += 2) {
    points.push(...rotate(shape.points[i], shape.points[i + 1]));
  }
  return { ...shape, points };
}

function PhotosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const cid = searchParams.get("cid");

  const [photo, setPhoto] = useState<HTMLCanvasElement | null>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [isModified, setIsModified] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState<keyof typeof SIZES>("M");

  const isDrawing = useRef(false);
  const stageRef = useRef<any>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const observer = new ResizeObserver(() => setBoxWidth(box.clientWidth));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    getPhoto(id || undefined).then(async (p) => {
      if (!p.photoData) return;
      setPhoto(await loadPhoto(p.photoData));
      setShapes([]);
      setIsModified(false);
      setSearchParams(
        (prev) => {
          prev.set("cid", p.customerID);
          return prev;
        },
        { replace: true }
      );
    });
  }, [id]);

  // Fit the photo in the available width and most of the screen height.
  const scale = photo
    ? Math.min(
        boxWidth / photo.width,
        (window.innerHeight * 0.7) / photo.height
      )
    : 1;
  const longSide = photo ? Math.max(photo.width, photo.height) : 1;
  const strokeWidth = SIZES[size] * longSide;

  const pointer = (e: any) => {
    const pos = e.target.getStage().getPointerPosition();
    return { x: pos.x / scale, y: pos.y / scale };
  };

  const handleDown = (e: any) => {
    if (!photo) return;
    // Stop the browser from starting a text selection or image drag.
    e.evt.preventDefault();
    const { x, y } = pointer(e);
    if (tool === "text") {
      const text = window.prompt("Label text");
      if (text) {
        setShapes([...shapes, { type: "text", x, y, text, color, width: strokeWidth }]);
        setIsModified(true);
      }
      return;
    }
    isDrawing.current = true;
    const points = tool === "arrow" ? [x, y, x, y] : [x, y];
    setShapes([...shapes, { type: tool, points, color, width: strokeWidth }]);
    setIsModified(true);
  };

  const handleMove = (e: any) => {
    if (!isDrawing.current) return;
    const { x, y } = pointer(e);
    const last = shapes[shapes.length - 1];
    if (last.type === "text") return;
    const points =
      last.type === "arrow"
        ? [last.points[0], last.points[1], x, y]
        : last.points.concat([x, y]);
    setShapes([...shapes.slice(0, -1), { ...last, points }]);
  };

  // A stroke ends only when the button is released, wherever the pointer is.
  useEffect(() => {
    const end = () => {
      isDrawing.current = false;
    };
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, []);

  const rotate = () => {
    if (!photo) return;
    setShapes(shapes.map((s) => rotateShape(s, photo.height)));
    setPhoto(rotateCanvas(photo));
    setIsModified(true);
  };

  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(await loadPhoto(URL.createObjectURL(file)));
    setShapes([]);
    setIsModified(true);
  };

  const navigate = useNavigate();
  const goBack = () => {
    if (isModified && !window.confirm("Leave without saving your changes?")) {
      return;
    }
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(`/customers?id=${cid}`);
    }
  };

  const removePhoto = async () => {
    if (!id || !window.confirm("Delete this photo? This can't be undone.")) {
      return;
    }
    await deletePhoto(id);
    navigate(`/customers?id=${cid}`, { replace: true });
  };

  const saveImage = async (asNew: boolean) => {
    if (!photo) return;
    setSaving(true);
    // Export at the photo's own resolution, not the screen size.
    const uri = stageRef.current.toDataURL({
      mimeType: "image/jpeg",
      quality: 0.9,
      pixelRatio: 1 / scale,
    });
    const saved = await savePhoto({
      id: asNew ? undefined : id || undefined,
      photoData: uri,
      customerID: cid || "",
    });
    setPhoto(await loadPhoto(saved.photoData));
    setShapes([]);
    setIsModified(false);
    setSaving(false);
    setSearchParams(
      (sp) => {
        sp.set("id", saved.id || "");
        return sp;
      },
      { replace: true }
    );
  };

  return (
    <div className="page">
      <TopNav pageTitle="Photo" />
      <main className={pageStyle.main}>
        <Button
          startIcon={<ArrowBack />}
          onClick={goBack}
          className={pageStyle.backButton}
        >
          Back to customer
        </Button>
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<Photo />}
        >
          {photo ? "Replace Photo" : "Add Photo"}
          <VisuallyHiddenInput type="file" accept="image/*" onChange={uploadImage} />
        </Button>

        {photo && (
          <div className={pageStyle.toolbar}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={tool}
              onChange={(_, t) => t && setTool(t)}
            >
              <ToggleButton value="pen" aria-label="Pen">
                <Tooltip title="Draw"><Gesture /></Tooltip>
              </ToggleButton>
              <ToggleButton value="arrow" aria-label="Arrow">
                <Tooltip title="Arrow"><NorthEast /></Tooltip>
              </ToggleButton>
              <ToggleButton value="text" aria-label="Text">
                <Tooltip title="Text label (tap where it goes)"><TextFields /></Tooltip>
              </ToggleButton>
              <ToggleButton value="eraser" aria-label="Eraser">
                <Tooltip title="Erase markings"><AutoFixNormal /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <div className={pageStyle.colors}>
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  className={pageStyle.swatch}
                  data-selected={c === color}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={size}
              onChange={(_, s) => s && setSize(s)}
            >
              {Object.keys(SIZES).map((s) => (
                <ToggleButton key={s} value={s} aria-label={`Size ${s}`}>
                  {s}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <div>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    disabled={shapes.length === 0}
                    onClick={() => setShapes(shapes.slice(0, -1))}
                  >
                    <Undo />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear all markings">
                <span>
                  <IconButton
                    disabled={shapes.length === 0}
                    onClick={() => setShapes([])}
                  >
                    <DeleteSweep />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Rotate">
                <IconButton onClick={rotate}>
                  <Rotate90DegreesCw />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}

        <div ref={boxRef} className={pageStyle.canvasBox}>
          {photo && boxWidth > 0 && (
            <Stage
              width={photo.width * scale}
              height={photo.height * scale}
              // Pointer events cover mouse, finger and stylus without the
              // duplicate mouse events phones send after a touch.
              onPointerDown={handleDown}
              onPointerMove={handleMove}
              ref={stageRef}
              style={{ touchAction: "none" }}
            >
              <Layer listening={false}>
                <KImage
                  image={photo}
                  width={photo.width * scale}
                  height={photo.height * scale}
                />
              </Layer>
              {/* Separate layer so the eraser removes markings, not the photo. */}
              {/* Markings ignore the pointer, so drawing over an earlier
                  line never interrupts the current stroke. */}
              <Layer scaleX={scale} scaleY={scale} listening={false}>
                {shapes.map((s, i) => {
                  if (s.type === "text") {
                    return (
                      <Text
                        key={i}
                        x={s.x}
                        y={s.y}
                        text={s.text}
                        fill={s.color}
                        fontSize={s.width * 3.5}
                        // Wrap at the photo's edge instead of running off it.
                        width={Math.max(photo.width - s.x, s.width * 20)}
                        fontStyle="bold"
                        stroke={s.color === "#000000" ? "#ffffff" : "#000000"}
                        strokeWidth={s.width / 4}
                      />
                    );
                  }
                  if (s.type === "arrow") {
                    return (
                      <Arrow
                        key={i}
                        points={s.points}
                        stroke={s.color}
                        fill={s.color}
                        strokeWidth={s.width}
                        pointerLength={s.width * 4}
                        pointerWidth={s.width * 4}
                        lineCap="round"
                      />
                    );
                  }
                  return (
                    <Line
                      key={i}
                      points={s.points}
                      stroke={s.color}
                      strokeWidth={s.type === "eraser" ? s.width * 3 : s.width}
                      tension={0.5}
                      lineCap="round"
                      lineJoin="round"
                      globalCompositeOperation={
                        s.type === "eraser" ? "destination-out" : "source-over"
                      }
                    />
                  );
                })}
              </Layer>
            </Stage>
          )}
        </div>

        <div className={pageStyle.saveButtons}>
          <Button
            disabled={!isModified || saving}
            variant="contained"
            color="success"
            onClick={() => saveImage(false)}
          >
            Save
          </Button>
          {id && (
            <Button
              disabled={!isModified || saving}
              variant="outlined"
              color="success"
              onClick={() => saveImage(true)}
            >
              Save as New Photo
            </Button>
          )}
          {id && (
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={removePhoto}
              disabled={saving}
              className={pageStyle.deleteButton}
            >
              Delete Photo
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

export default PhotosPage;
